import CarManagmentProvider, {
  useCarManagmentContext
} from 'providers/CarManagmentProvider';
import dayjs /* , { Dayjs }*/ from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
require('dayjs/locale/pt');
dayjs.extend(localeData);
dayjs.extend(LocalizedFormat);
dayjs.extend(weekday);
// import 'flatpickr/dist/themes/material_blue.css'; // or any theme
import BasicGanttChart, {
  Trip
} from 'components/charts/dhtmlx/BasicGanttChart';
import { useCallback, useMemo } from 'react';
import CalendarAddNewEventModal from 'components/modals/CarManagementAddNewTripModal';
import CalendarProvider from 'providers/CalendarProvider';
import { getPlannerPrefetchRange } from 'helpers/utils';

const index = () => {
  return (
    <CalendarProvider loadTripsOnMount={false}>
      <CarManagmentProvider loadTripsOnMount={false}>
        <Calendar />
      </CarManagmentProvider>
    </CalendarProvider>
  );
};

const Calendar = () => {
  const { plannerTrips, fetchTripsForRange, isLoadingTripRange, tripsError } =
    useCarManagmentContext();

  const handlePlannerDateChange = useCallback(
    (date: Date) => {
      void fetchTripsForRange(getPlannerPrefetchRange(date));
    },
    [fetchTripsForRange]
  );

  // const confirmedTrips = useMemo(() => {
  //   return (trips || [])
  //     .filter(trip => trip.status === 'Aprovado')
  //     .map(
  //       trip =>
  //         ({
  //           id: trip.id as number,
  //           text: trip.title,
  //           passenger: trip.passenger || trip.organizer || 'Unknown',
  //           start_date: new Date(trip.start),
  //           duration: trip.end
  //             ? (new Date(trip.end).getTime() -
  //                 new Date(trip.start).getTime()) /
  //               (1000 * 60 * 60)
  //             : 1,
  //           carPlate: trip.carPlate,
  //           originalStart: new Date(trip.start),
  //           originalEnd: new Date(trip.end || trip.start),
  //           isClipped: trip.takecar === 'Y' || trip.leavecar === 'N',
  //           isConfirmed: true,
  //           takeCar: trip.takecar,
  //           leaveCar: trip.leavecar
  //         }) as Trip
  //     );
  // }, [trips]);

  const confirmedTrips = useMemo(() => {
    const tripsByCar = new Map<
      string,
      { trip: (typeof plannerTrips)[number]; start: Date; end: Date }[]
    >();
    const unassignedTrips: Trip[] = [];

    [...plannerTrips]
      .reverse()
      .filter(trip => trip.status === 'Aprovado')
      .forEach(trip => {
        const start = new Date(trip.start);
        const suppliedEnd = new Date(trip.end || trip.start);
        const end =
          suppliedEnd > start
            ? suppliedEnd
            : new Date(start.getTime() + (trip.duration || 1) * 60 * 60 * 1000);
        const carPlate = trip.carPlate;

        if (!carPlate) {
          unassignedTrips.push({
            id: trip.id,
            grupo: trip.grupo,
            text: trip.title,
            passenger: trip.passenger || trip.organizer || 'Unknown',
            listPassenger: trip.usersList,
            outsideUsers: trip.outsideUsers,
            start_date: start,
            end_date: end,
            duration: (end.getTime() - start.getTime()) / (1000 * 60 * 60),
            carPlate: trip.carPlate || '',
            originalStart: start,
            originalEnd: end,
            isClipped: trip.takecar === 'Y' || trip.leavecar === 'N',
            isConfirmed: true,
            takeCar: trip.takecar,
            leaveCar: trip.leavecar
          } as Trip);
          return;
        }

        const carTrips = tripsByCar.get(carPlate) || [];
        carTrips.push({ trip, start, end });
        tripsByCar.set(carPlate, carTrips);
      });

    const groupedTrips: Trip[] = [...tripsByCar.values()].flatMap(carTrips => {
      carTrips.sort(
        (a, b) =>
          a.start.getTime() - b.start.getTime() ||
          a.end.getTime() - b.end.getTime()
      );

      const groups: (typeof carTrips)[] = [];
      let currentGroup: typeof carTrips = [];
      let currentEnd: Date | null = null;

      carTrips.forEach(item => {
        if (currentEnd && item.start < currentEnd) {
          currentGroup.push(item);
          if (item.end > currentEnd) currentEnd = item.end;
          return;
        }

        if (currentGroup.length > 0) groups.push(currentGroup);
        currentGroup = [item];
        currentEnd = item.end;
      });
      if (currentGroup.length > 0) groups.push(currentGroup);

      return groups.map(group => {
        const first = group[0];
        const start = first.start;
        const end = group.reduce(
          (latestEnd, item) => (item.end > latestEnd ? item.end : latestEnd),
          first.end
        );
        const passengerNames = Array.from(
          new Set(
            group
              .map(({ trip }) => trip.passenger || trip.organizer || 'Unknown')
              .flatMap(passenger =>
                passenger
                  .split(',')
                  .map(name => name.trim())
                  .filter(Boolean)
              )
          )
        );
        const passengerLists = Array.from(
          new Set(
            group
              .flatMap(({ trip }) => (trip.usersList || '').split(','))
              .map(name => name.trim())
              .filter(Boolean)
          )
        );
        const tripLocations = Array.from(
          new Set(
            group
              .map(({ trip }) => trip.title?.trim())
              .filter((location): location is string => Boolean(location))
          )
        );

        return {
          id: first.trip.id,
          grupo: first.trip.grupo,
          text: tripLocations.join(', '),
          passenger: passengerNames.join(', '),
          listPassenger: passengerLists.join(', '),
          outsideUsers: first.trip.outsideUsers,
          start_date: start,
          end_date: end,
          duration: (end.getTime() - start.getTime()) / (1000 * 60 * 60),
          carPlate: first.trip.carPlate || '',
          originalStart: start,
          originalEnd: end,
          isClipped: group.some(
            ({ trip }) => trip.takecar === 'Y' || trip.leavecar === 'N'
          ),
          isConfirmed: true,
          takeCar: first.trip.takecar,
          leaveCar: first.trip.leavecar
        } as Trip;
      });
    });

    return [...groupedTrips, ...unassignedTrips];
  }, [plannerTrips]);

  return (
    <div>
      <BasicGanttChart
        trips={confirmedTrips}
        rightColumn="carPlate"
        initialViewMode={'week'}
        onPlannerDateChange={handlePlannerDateChange}
      />
      {tripsError && !isLoadingTripRange && (
        <div className="alert alert-danger" role="alert">
          {tripsError}
        </div>
      )}
      <CalendarAddNewEventModal />
    </div>
  );
};

export default index;
